import { useState } from 'react';
import { X, AlertCircle, Loader, Tag } from 'lucide-react';
import { calendarApiService } from '../services/calendarApi';
import { motion, AnimatePresence } from 'framer-motion';

interface CreateCalendarTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateCalendarTagModal = ({ isOpen, onClose, onSuccess }: CreateCalendarTagModalProps) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3498db');
  const [visibility, setVisibility] = useState<'public' | 'restricted'>('public');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await calendarApiService.createTag({
        name,
        color,
        visibility,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setColor('#3498db');
    setVisibility('public');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="glass-card p-4 md:p-6 w-full max-w-2xl max-md:max-w-[95vw]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Tag className="w-6 h-6 text-accent-400" />
              <h2 className="text-lg font-semibold tracking-tight text-white">Create Calendar Tag</h2>
            </div>
            <button onClick={handleClose} className="glass-button p-2 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="glass-card p-4 mb-4 border-l-4 border-red-400 bg-red-500/10">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="glass-input w-full"
                placeholder="Tag name..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Color <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-16 h-12 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="glass-input flex-1 font-mono"
                  placeholder="#3498db"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Visibility
              </label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'public' | 'restricted')}
                className="glass-input w-full"
              >
                <option value="public">Public</option>
                <option value="restricted">Restricted</option>
              </select>
              <p className="text-xs text-glass-400 mt-1">
                Restricted tags require specific permission to view
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="glass-button-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="glass-button"
                disabled={isSubmitting || !name}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Tag'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

